/**
 * 세션 관리 유틸리티
 * 사용자 활동을 추적하고 비활성 시간이 지정된 시간을 초과하면 세션 만료 처리
 */

const DEFAULT_SESSION_TIMEOUT = 4 * 60 * 60 * 1000; // 4시간 (밀리초)
const REMEMBER_ME_SESSION_TIMEOUT = 12 * 60 * 60 * 1000; // 12시간 (밀리초)
const LAST_ACTIVITY_KEY = 'lastActivityTime';

export interface SessionManagerOptions {
  onSessionExpired: () => void;
  isRememberMe?: boolean;
}

/**
 * 세션 관리자 초기화
 * 사용자 활동을 추적하고 일정 시간 비활성 시 세션 만료 콜백 호출
 */
export const initSessionManager = (options: SessionManagerOptions) => {
  const { onSessionExpired, isRememberMe = false } = options;
  
  let activityTimer: NodeJS.Timeout | null = null;
  const sessionTimeout = isRememberMe 
    ? REMEMBER_ME_SESSION_TIMEOUT 
    : DEFAULT_SESSION_TIMEOUT;

  /**
   * 타이머 리셋
   * 사용자 활동이 감지되면 마지막 활동 시간을 업데이트하고 타이머를 재설정
   */
  const resetTimer = () => {
    if (activityTimer) {
      clearTimeout(activityTimer);
    }
    
    const now = Date.now();
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    
    activityTimer = setTimeout(() => {
      console.log(`⏰ 세션 만료: ${isRememberMe ? '12시간' : '4시간'} 비활성 시간 초과`);
      onSessionExpired();
    }, sessionTimeout);
  };

  /**
   * 마지막 활동 시간 확인 및 타이머 설정
   */
  const startSessionTracking = () => {
    const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY);
    
    if (lastActivityStr) {
      const lastActivity = parseInt(lastActivityStr, 10);
      const inactiveTime = Date.now() - lastActivity;
      
      if (inactiveTime >= sessionTimeout) {
        // 이미 세션이 만료됨
        console.log('⏰ 세션 만료: 마지막 활동 이후 시간 초과');
        onSessionExpired();
        return;
      }
      
      // 남은 시간만큼 타이머 설정
      const remainingTime = sessionTimeout - inactiveTime;
      activityTimer = setTimeout(() => {
        console.log('⏰ 세션 만료: 비활성 시간 초과');
        onSessionExpired();
      }, remainingTime);
      
      console.log(`⏰ 세션 추적 시작: ${Math.floor(remainingTime / 1000 / 60)}분 후 만료 예정`);
    } else {
      // 처음 시작하는 경우
      resetTimer();
      console.log(`⏰ 세션 추적 시작: ${isRememberMe ? '12시간' : '4시간'} 후 만료 예정`);
    }
  };

  /**
   * 사용자 활동 감지 이벤트 리스너
   */
  const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
  
  const handleActivity = () => {
    resetTimer();
  };

  // 이벤트 리스너 등록
  activityEvents.forEach(event => {
    document.addEventListener(event, handleActivity, { passive: true });
  });

  // 탭/윈도우 포커스 이벤트도 감지
  const handleFocus = () => {
    // 포커스가 돌아왔을 때 마지막 활동 시간 확인
    const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (lastActivityStr) {
      const lastActivity = parseInt(lastActivityStr, 10);
      const inactiveTime = Date.now() - lastActivity;
      
      if (inactiveTime >= sessionTimeout) {
        // 다른 탭에서 돌아왔는데 세션이 만료된 경우
        onSessionExpired();
        return;
      }
      // 타이머 재설정
      resetTimer();
    }
  };

  window.addEventListener('focus', handleFocus);

  // 초기 세션 추적 시작
  startSessionTracking();

  /**
   * 정리 함수
   * 컴포넌트 언마운트 시 호출되어 이벤트 리스너와 타이머 제거
   */
  const cleanup = () => {
    if (activityTimer) {
      clearTimeout(activityTimer);
      activityTimer = null;
    }
    
    activityEvents.forEach(event => {
      document.removeEventListener(event, handleActivity);
    });
    
    window.removeEventListener('focus', handleFocus);
    
    // 마지막 활동 시간은 유지 (페이지 새로고침 시에도 추적을 위해)
    // 완전히 로그아웃할 때만 삭제됨
  };

  return cleanup;
};

/**
 * 세션 정보 초기화
 * 로그아웃 시 호출하여 세션 추적 데이터 정리
 */
export const clearSessionData = () => {
  localStorage.removeItem(LAST_ACTIVITY_KEY);
  console.log('🧹 세션 데이터 정리 완료');
};

/**
 * 현재 세션 상태 확인
 * @returns 만료까지 남은 시간(밀리초), 또는 null (세션 없음)
 */
export const getRemainingSessionTime = (isRememberMe: boolean = false): number | null => {
  const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY);
  if (!lastActivityStr) {
    return null;
  }
  
  const lastActivity = parseInt(lastActivityStr, 10);
  const sessionTimeout = isRememberMe 
    ? REMEMBER_ME_SESSION_TIMEOUT 
    : DEFAULT_SESSION_TIMEOUT;
  
  const inactiveTime = Date.now() - lastActivity;
  const remainingTime = sessionTimeout - inactiveTime;
  
  return remainingTime > 0 ? remainingTime : 0;
};

